import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../config/firebase';
import { authService } from '../services/authService';
import {
  resolveClinicUserData,
  resolveSessionIdentity,
  applyClinicAdminPrivileges,
  enrichFromUsersDocument,
} from '../utils/loginRestrictions';
import { syncUserClinicToFirestore } from '../utils/syncUserClinicToFirestore';

export const AuthContext = createContext({
  user: null,
  userData: null,
  loading: true,
  permissionsLoaded: false,
  signIn: () => {},
  completeSuperadminMfaSignIn: async () => {},
  signOut: () => {},
  signInWithUsername: () => {},
  refreshUserData: async () => null,
});

const SESSION_KEY_USER = 'clinic_user';
const SESSION_KEY_DATA = 'clinic_user_data';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          localStorage.removeItem(SESSION_KEY_USER);
          localStorage.removeItem(SESSION_KEY_DATA);
          setUser(firebaseUser);

          const minimalBase = {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            isActive: true,
          };

          // users/{uid} is authoritative for clinic_admin role — load in parallel with full profile
          const [firestoreData, usersEnriched, clinicResolved] = await Promise.all([
            Promise.race([
              authService.getCurrentUserData(),
              new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 12000)),
            ]).catch(() => null),
            enrichFromUsersDocument({ ...minimalBase, role: 'clinic_staff' }),
            resolveClinicUserData({ ...minimalBase, role: 'clinic_staff' }),
          ]);

          let data = firestoreData || usersEnriched || clinicResolved;
          if (!data && firebaseUser.email) {
            data = { ...minimalBase, role: 'clinic_staff' };
          }

          data.uid = data.uid || firebaseUser.uid;
          if (firebaseUser.emailVerified || data.emailVerified === true) {
            data.emailVerified = true;
          }
          if (!data.role || (data.role === 'clinic_staff' && usersEnriched?.role === 'clinic_admin')) {
            data.role = usersEnriched?.role || firestoreData?.role || data.role || 'clinic_staff';
          }
          if (!data.clinicId && (clinicResolved?.clinicId || usersEnriched?.clinicId)) {
            data.clinicId = clinicResolved?.clinicId || usersEnriched?.clinicId;
            data.clinicName = clinicResolved?.clinicName || usersEnriched?.clinicName || data.clinicName;
          }
          data = await resolveClinicUserData(data);
          data = applyClinicAdminPrivileges(data);
          data = await resolveSessionIdentity(data, firebaseUser);
          await syncUserClinicToFirestore(data);
          setUserData(data);
          setLoading(false);
          setPermissionsLoaded(true);
        } else {
          const storedUser = localStorage.getItem(SESSION_KEY_USER);
          const storedData = localStorage.getItem(SESSION_KEY_DATA);
          if (storedUser && storedData) {
            const parsedUser = JSON.parse(storedUser);
            let parsedData = JSON.parse(storedData);
            parsedData.uid = parsedData.uid || parsedUser?.uid;
            parsedData.role = parsedData.role || 'clinic_staff';
            if (!parsedData.clinicId && parsedData.role !== 'super_admin') {
              parsedData = await resolveClinicUserData(parsedData);
            }
            parsedData = applyClinicAdminPrivileges(parsedData);
            parsedData = await resolveSessionIdentity(parsedData, parsedUser);
            setUser(parsedUser);
            setUserData(parsedData);
          } else {
            setUser(null);
            setUserData(null);
          }
          setLoading(false);
          setPermissionsLoaded(true);
        }
      } catch (err) {
        console.error('AuthProvider error:', err);
        if (firebaseUser) {
          setUser(firebaseUser);
          try {
            setUserData(
              await resolveSessionIdentity(
                applyClinicAdminPrivileges(
                  await resolveClinicUserData({
                    uid: firebaseUser.uid,
                    email: firebaseUser.email,
                    displayName: firebaseUser.displayName,
                    isActive: true,
                  })
                ),
                firebaseUser
              )
            );
          } catch (e) {
            setUserData({ uid: firebaseUser.uid, email: firebaseUser.email, role: 'clinic_staff', isActive: true });
          }
        } else {
          setUser(null);
          setUserData(null);
        }
        setLoading(false);
        setPermissionsLoaded(true);
      }
    });
    return () => unsubscribe();
  }, []);

  const signIn = async (email, password) => {
    const result = await authService.signIn(email, password);
    return result;
  };

  const completeSuperadminMfaSignIn = async (firebaseError, verificationCode) => {
    return authService.completeMfaSignIn(firebaseError, verificationCode);
  };

  const signInWithUsername = async (username, password) => {
    const result = await authService.signInWithUsername(username, password);
    let sessionData = result.userData;
    sessionData.uid = sessionData.uid || result.user?.uid;
    sessionData = await resolveClinicUserData(sessionData);
    sessionData = applyClinicAdminPrivileges(sessionData);
    sessionData = await resolveSessionIdentity(sessionData, result.user);
    await syncUserClinicToFirestore(sessionData);
    localStorage.setItem(SESSION_KEY_USER, JSON.stringify(result.user));
    localStorage.setItem(SESSION_KEY_DATA, JSON.stringify(sessionData));
    setUser(result.user);
    setUserData(sessionData);
    setPermissionsLoaded(true);
    setLoading(false);
    return result;
  };

  const signOut = async () => {
    localStorage.removeItem(SESSION_KEY_USER);
    localStorage.removeItem(SESSION_KEY_DATA);
    setUser(null);
    setUserData(null);
    await authService.signOut();
  };

  const refreshUserData = useCallback(async ({ reloadAuth = true } = {}) => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) return null;
    try {
      if (reloadAuth) {
        try {
          await firebaseUser.reload();
          await firebaseUser.getIdToken(true);
        } catch (_) {
          /* ignore stale-token reload failures */
        }
      }
      const freshUser = auth.currentUser || firebaseUser;
      setUser(freshUser);
      let data = await authService.getCurrentUserData();
      if (!data) return null;
      data.uid = data.uid || freshUser.uid;
      data.role = data.role || 'clinic_staff';
      // Keep client profile aligned with Auth so verify banners can hide reliably
      if (freshUser.emailVerified) {
        data.emailVerified = true;
      }
      data = await resolveClinicUserData(data);
      data = applyClinicAdminPrivileges(data);
      data = await resolveSessionIdentity(data, freshUser);
      await syncUserClinicToFirestore(data);
      setUserData(data);
      const stored = localStorage.getItem(SESSION_KEY_DATA);
      if (stored) {
        try {
          localStorage.setItem(SESSION_KEY_DATA, JSON.stringify(data));
        } catch (_) {}
      }
      return data;
    } catch (e) {
      console.error('refreshUserData:', e);
      return null;
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      userData,
      loading,
      permissionsLoaded,
      signIn,
      completeSuperadminMfaSignIn,
      signOut,
      signInWithUsername,
      refreshUserData,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
